/**
 * @fileoverview Route-level permission guard.
 *
 * แก้ปัญหา: user ที่ไม่มี permission ของเมนู ยังสามารถพิมพ์ URL เข้าหน้าได้โดยตรง
 * (เมนูถูกซ่อนแล้วจาก Navbar แต่ route ไม่ได้ตรวจสิทธิ์)
 *
 * วิธีการ: ใช้ "แหล่งความจริงเดียว" (single source of truth) คือ map ของ
 * link -> permission ที่ประกาศไว้แล้วใน global.js (global.menu_admin/customer/
 * warehouse/super_user) มาตรวจสิทธิ์ตอนเข้าถึง route โดยตรง
 *
 * ใช้เป็น layout route ของ react-router v6:
 *   <Route element={<PermissionRoute />}>
 *     <Route path="/ad/dashboard" element={<AdminDashboard />} />
 *     ...
 *   </Route>
 */

import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
// ensure global.menu_* are populated (idempotent import)
import "../global.js";
import { saveReturnPath } from "../../utils/returnPath.js";
import { readProfile, currentPortal, isPortalAllowed, isTechnicianProfile, allowedWebPortals, effectivePermissions } from "../../utils/portalAccess.js";

/**
 * สร้าง map ของ pathname -> permission ที่ต้องใช้ จากนิยามเมนูใน global.js
 * @returns {Object<string,string>}
 */
const buildPermissionMap = () => {
  const map = {};
  const menus = [
    ...(global.menu_admin || []),
    ...(global.menu_customer || []),
    ...(global.menu_warehouse || []),
    ...(global.menu_super_user || []),
  ];
  menus.forEach((item) => {
    if (item.link && item.permission) {
      const path = item.link.split("?")[0]; // ตัด query string ออก
      map[path] = item.permission;
    }
  });
  return map;
};

// path -> รายการเมนู (ไว้อ่าน userTypes ของเมนูที่ล็อกด้วยประเภทผู้ใช้ เช่น "ตั้งค่าระบบ")
const buildMenuItemMap = () => {
  const map = {};
  const menus = [
    ...(global.menu_admin || []),
    ...(global.menu_customer || []),
    ...(global.menu_warehouse || []),
    ...(global.menu_super_user || []),
  ];
  menus.forEach((item) => {
    if (item.link) map[item.link.split("?")[0]] = item;
  });
  return map;
};

/**
 * หน้าแรกของพอร์ทัลที่ประเภทผู้ใช้เข้าได้ - ใช้เด้งคนที่พิมพ์ URL ข้ามพอร์ทัล
 * @param {Object} profile
 * @returns {string}
 */
const fallbackPortalLink = (profile) => {
  if (isTechnicianProfile(profile)) return "/mb/mobile";
  const perms = profile?.role?.permission || [];
  const menus = { admin: global.menu_admin, warehouse: global.menu_warehouse, customer: global.menu_customer };
  for (const portal of allowedWebPortals(profile)) {
    const link = global.getFirstAccessibleLink(menus[portal], perms, profile);
    if (link) return link;
  }
  return "/forbidden";
};

const getProfile =() => readProfile();

const PermissionRoute = () => {
  const location = useLocation();
  const token = localStorage.getItem("cm-acc-tk");
  const profile = getProfile();

  // 1) ยังไม่ได้ login -> ไปหน้า login
  if (!token || !profile) {
    saveReturnPath(location);
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  // 2) บัญชีระดับแพลตฟอร์ม/บริษัท ข้ามการตรวจสิทธิ์ระดับหน้า
  //    (สอดคล้องกับการ special-case ที่มีอยู่เดิมใน Navbar/console)
  if (["super_user", "admin-company"].includes(profile.user_type)) {
    // admin-company ข้ามเช็ค permission ได้ แต่เมนูที่ล็อกประเภทผู้ใช้ (ตั้งค่าระบบ) ยังต้องตรงประเภท
    const lockedItem = profile.user_type === "admin-company" ? buildMenuItemMap()[location.pathname] : null;
    if (lockedItem && global.menuItemTypeAccess(lockedItem, profile) === false) {
      return <Navigate to="/forbidden" replace />;
    }
    return <Outlet />;
  }

  // 3) พอร์ทัลตามประเภทผู้ใช้ (master user_types -> profile.user_type_info.portals): เข้าได้เฉพาะพอร์ทัลที่ master ให้
  //    แม้ role จะมี permission (กันพิมพ์ URL ข้ามพอร์ทัล) - ช่างทุกประเภทเข้าได้แค่ /mb
  const portal = currentPortal(location.pathname);
  if (portal && !isPortalAllowed(profile, portal)) {
    return <Navigate to={fallbackPortalLink(profile)} replace />;
  }

  const required = buildPermissionMap()[location.pathname];

  // 4) ไม่มีการประกาศ permission สำหรับ path นี้ -> ปล่อยผ่าน (เช่นหน้าที่ไม่อยู่ในเมนู)
  if (!required) {
    return <Outlet />;
  }

  // 5) เมนูที่คุมด้วยประเภทผู้ใช้ (เช่น "ตั้งค่าระบบ" = super_user / it_admin) - ตัดสินด้วยประเภทอย่างเดียว ไม่ดู permission ของ role
  //    super_user ผ่านไปแล้วที่ข้อ 2 จึงเหลือเช็คประเภทอื่น
  const typeAccess = global.menuItemTypeAccess(buildMenuItemMap()[location.pathname], profile);
  if (typeAccess !== null) {
    return typeAccess ? <Outlet /> : <Navigate to="/forbidden" replace />;
  }

  // 6) ตรวจสิทธิ์จาก permission ของ role (พอร์ทัลดูอย่างเดียวเหลือ .read ซึ่งเมนูใช้อยู่แล้ว)
  const perms = effectivePermissions(profile, portal);
  const menuItem = buildMenuItemMap()[location.pathname];
  if (perms.includes(required) || (menuItem?.permissionAny || []).some((key) => perms.includes(key))) {
    return <Outlet />;
  }

  // 7) ไม่มีสิทธิ์ -> หน้า 403 (ไม่ใช่ 404 เพราะหน้ามีอยู่จริง แค่ role ไม่ถึง)
  //    แยกให้ชัดว่าเป็นปัญหาสิทธิ์ ไม่ใช่ปัญหา route หาย
  return <Navigate to="/forbidden" replace />;
};

export default PermissionRoute;
